
import { useState } from "react";
import { Airport } from "@/types/airport";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type SortField = 'name' | 'iata_code' | 'location';

export function useAirportManagement(airports: Airport[]) {
  const [sortField, setSortField] = useState<SortField>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [isDeleting, setIsDeleting] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const checkAdminPermission = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error("Authentication required");
        return null;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', user.id)
        .single();

      if (!profile?.is_admin) {
        toast.error("Admin permission required");
        return null;
      }

      return user.id;
    } catch (error) {
      console.error("Permission check error:", error);
      toast.error("Failed to verify admin permissions");
      return null;
    }
  };

  const handleUpdateAirport = async (airportId: number, updatedData: Partial<Airport>) => {
    setIsUpdating(true);
    try {
      const userId = await checkAdminPermission();
      if (!userId) return false;

      console.log("Updating airport with ID:", airportId, updatedData);

      // Use a type assertion to work around the TypeScript constraint
      // @ts-ignore - We know this function exists in the Supabase backend
      const { data, error } = await supabase.rpc('update_airport', {
        p_id: airportId,
        p_name: updatedData.name!,
        p_iata_code: updatedData.iata_code || '',
        p_location: updatedData.location!,
        p_latitude: updatedData.latitude!,
        p_longitude: updatedData.longitude!
      });

      if (error) throw error;
      if (!data) throw new Error('Update failed');

      toast.success("Airport updated successfully");
      return true;
    } catch (error: any) {
      toast.error("Failed to update airport");
      console.error("Update error:", error);
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteAirport = async (airportId: number) => {
    if (!confirm("Are you sure you want to delete this airport? This action cannot be undone.")) {
      return false;
    }

    setIsDeleting(true);
    try {
      const userId = await checkAdminPermission();
      if (!userId) return false;

      const { error } = await supabase
        .from('airports')
        .delete()
        .eq('id', airportId);

      if (error) throw error;

      toast.success("Airport deleted successfully");
      return true;
    } catch (error: any) {
      toast.error("Failed to delete airport");
      console.error("Delete error:", error);
      return false;
    } finally {
      setIsDeleting(false);
    }
  };

  const sortedAirports = [...airports].sort((a, b) => {
    const aValue = a[sortField] || '';
    const bValue = b[sortField] || '';
    const multiplier = sortDirection === 'asc' ? 1 : -1;
    return multiplier * aValue.toString().localeCompare(bValue?.toString() || '');
  });

  return {
    sortField,
    sortDirection,
    isDeleting,
    isUpdating,
    sortedAirports,
    handleSort,
    handleDeleteAirport,
    handleUpdateAirport,
  };
}
